import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { downloadQuotePdf } from '../utils/quotePdf';
import { formatPricePerGallon } from '../utils/pricing';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function formatCurrency(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTaxPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n}%`;
}

function getStatusColor(status) {
  const colors = {
    draft: 'default',
    submitted: 'info',
    accepted: 'success',
    rejected: 'error',
    expired: 'warning',
  };
  return colors[status] || 'default';
}

function formatFrequency(freq) {
  if (!freq) return '—';
  return freq
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-');
}

function formatFuelLineCalculation(line) {
  const qty = Number(line.quantity_gallons) || 0;
  const unit = Number(line.unit_price_per_gallon) || 0;
  const subtotal = Number(line.subtotal) || 0;
  const taxPercent = Number(line.tax_percent) || 0;
  const taxAmount = Number(line.tax_amount) || 0;
  const lineTotal = Number(line.line_total) || 0;

  let text = `${qty.toFixed(2)} gal × $${formatPricePerGallon(unit)}/gal = ${formatCurrency(subtotal)}`;
  if (taxPercent > 0) {
    text += ` + ${formatTaxPercent(taxPercent)} tax (${formatCurrency(taxAmount)}) = ${formatCurrency(lineTotal)}`;
  }
  return text;
}

function DetailItem({ label, value, fullWidth = false }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.25 }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );
}

function QuotePriceBreakdownSection({ breakdown }) {
  if (!breakdown) return null;

  const lines = breakdown.lines || [];
  const fuelPricing = breakdown.fuel_pricing;
  const hasFuelLines = lines.length > 0;
  const deliveryFee = Number(breakdown.delivery_fee) || 0;

  if (!hasFuelLines && deliveryFee <= 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Price breakdown
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No fuel quantities on this quote.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Price breakdown
      </Typography>

      {fuelPricing && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Unit prices: Diesel ${formatPricePerGallon(fuelPricing.diesel_price_per_gallon)}/gal (
          {formatTaxPercent(fuelPricing.diesel_tax_percent)} tax) · DEF $
          {formatPricePerGallon(fuelPricing.def_price_per_gallon)}/gal (
          {formatTaxPercent(fuelPricing.def_tax_percent)} tax) · Dyed $
          {formatPricePerGallon(fuelPricing.dyed_price_per_gallon)}/gal (
          {formatTaxPercent(fuelPricing.dyed_tax_percent)} tax)
        </Typography>
      )}

      {hasFuelLines && (
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fuel</TableCell>
                <TableCell align="right">Qty (gal)</TableCell>
                <TableCell align="right">Unit price</TableCell>
                <TableCell align="right">Fuel cost</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Line total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.fuel_type}>
                  <TableCell>{line.label}</TableCell>
                  <TableCell align="right">{Number(line.quantity_gallons).toFixed(2)}</TableCell>
                  <TableCell align="right">${formatPricePerGallon(line.unit_price_per_gallon)}/gal</TableCell>
                  <TableCell align="right">{formatCurrency(line.subtotal)}</TableCell>
                  <TableCell align="right">
                    {Number(line.tax_percent) > 0
                      ? `${formatTaxPercent(line.tax_percent)} (${formatCurrency(line.tax_amount)})`
                      : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(line.line_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasFuelLines && (
        <Box sx={{ mb: 1.5 }}>
          {lines.map((line) => (
            <Typography key={`calc-${line.fuel_type}`} variant="caption" color="text.secondary" display="block">
              {line.label}: {formatFuelLineCalculation(line)}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        {hasFuelLines && (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Fuel subtotal:</strong> {formatCurrency(breakdown.fuel_subtotal)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Sales tax:</strong> {formatCurrency(breakdown.tax_total)}
            </Typography>
          </>
        )}
        {deliveryFee > 0 && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Delivery fee:</strong> {formatCurrency(deliveryFee)}
          </Typography>
        )}
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
          Estimated total: {formatCurrency(breakdown.total)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function QuoteDetailsDialog({
  open,
  onClose,
  quote: initialQuote,
  showCustomerName = false,
  onAccept,
  onUploadPdf,
  uploadingPdf = false,
  fullScreen = false,
}) {
  const [quote, setQuote] = useState(initialQuote);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      setError('');
      return;
    }

    if (!initialQuote?.id) {
      setQuote(initialQuote);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/customers/quotes/${initialQuote.id}/`);
        if (!cancelled) setQuote(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              'Failed to load quote details.'
          );
          setQuote(initialQuote);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, initialQuote]);

  if (!initialQuote) return null;

  const handleDownload = async (e) => {
    e?.stopPropagation?.();
    setDownloading(true);
    try {
      await downloadQuotePdf(quote.id);
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      alert('Failed to download quote PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const fuelParts = [];
  if (Number(quote.diesel_quantity) > 0) {
    fuelParts.push(`${parseFloat(quote.diesel_quantity).toFixed(2)} gal Diesel`);
  }
  if (Number(quote.def_quantity) > 0) {
    fuelParts.push(`${parseFloat(quote.def_quantity).toFixed(2)} gal DEF`);
  }
  if (Number(quote.dyed_quantity) > 0) {
    fuelParts.push(`${parseFloat(quote.dyed_quantity).toFixed(2)} gal Dyed`);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        Quote {String(quote.id).substring(0, 8)}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={quote.status || 'unknown'} color={getStatusColor(quote.status)} size="small" />
          {quote.has_uploaded_pdf && (
            <Chip label="Uploaded PDF" size="small" variant="outlined" color="secondary" />
          )}
        </Box>

        <Grid container spacing={2}>
          {showCustomerName && (
            <DetailItem
              label="Customer"
              value={quote.customer_name || quote.quote_company_name || '—'}
              fullWidth
            />
          )}
          <DetailItem label="Frequency" value={formatFrequency(quote.frequency)} />
          <DetailItem label="Estimated price" value={formatCurrency(quote.estimated_price)} />
          <DetailItem label="Delivery fee" value={formatCurrency(quote.delivery_fee)} />
          <DetailItem
            label="Fuel quantities"
            value={fuelParts.length ? fuelParts.join(' / ') : 'None specified'}
            fullWidth
          />
          <DetailItem label="Delivery address" value={quote.delivery_address} fullWidth />
          <DetailItem label="Expires" value={formatDate(quote.expires_at)} />
          <DetailItem label="Created" value={formatDate(quote.created_at)} />
          {quote.updated_at && (
            <DetailItem label="Last updated" value={formatDate(quote.updated_at)} />
          )}
        </Grid>

        <QuotePriceBreakdownSection breakdown={quote?.price_breakdown} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ flexGrow: 1 }} />
        {onUploadPdf && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<UploadFileIcon />}
            disabled={uploadingPdf}
            onClick={(e) => {
              e.stopPropagation();
              onUploadPdf(quote);
            }}
          >
            {quote.has_uploaded_pdf ? 'Replace PDF' : 'Upload PDF'}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? 'Downloading…' : 'Download PDF'}
        </Button>
        {onAccept && quote?.status === 'submitted' && (
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onAccept(quote);
            }}
          >
            Accept quote
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
